import { FC, ReactNode } from 'react';
import { connect, EasyField, useForm } from 'react-bootstrap-formutil';
import FormGroup from 'components/FormGroup';
import BaseAddress from 'components/forms/AddressInfo';
import CountrySelect from 'components/forms/CountrySelect';
import type { FormCheckType } from 'types/common';
import type { CustomerInfoKeyType } from 'types/customer';
import DeliveryCountry from '../components/DeliveryCountry';
import { BaseDeliveryAddressProps, DeliveryAddressTypeEnum } from '../type';

export interface USADeliveryAddressFormutil {
    delivery_address: string;
    delivery_address2: string;
    delivery_city: string;
    delivery_state_code: string;
    delivery_postal: string;
    delivery_country: string;
    delivery_address_type: DeliveryAddressTypeEnum;
}

export interface USADeliveryAddressProps extends BaseDeliveryAddressProps {
    isMarsco?: boolean;
    title?: ReactNode;
    isSSN: boolean;
    isFourChn?: boolean;
}

/**
 * @description 获取邮寄地址对应的默认表单项字段的数组
 * @param sameDeliveryAndResidence same_delivery_and_residence，我的邮寄地址与居住地址不同对应的字段
 */
export function getFieldsOfUSADeliveryAddress(
    sameDeliveryAndResidence: FormCheckType | undefined,
    needDeliveryAddressType: boolean = true
) {
    const baseFields: CustomerInfoKeyType[] =
        sameDeliveryAndResidence === '0'
            ? [
                  'delivery_address',
                  'delivery_address2',
                  'delivery_city',
                  'delivery_state_code',
                  'delivery_postal',
                  'delivery_country'
              ]
            : [];

    return needDeliveryAddressType ? baseFields.concat('delivery_address_type') : baseFields;
}

/**
 * @description USA邮寄地址类型，此组件需要同时支持SSN+非SSN开户流程的静态展示/切换邮寄国家功能
 */
const USA: FC<USADeliveryAddressProps> = ({
    title = __('MAILING ADDRESS'),
    hasCountry,
    deliveryCountry,
    countryDisabled,
    disabled,
    isSSN,
    isFourChn,
    ...restProps
}) => {
    const { $setValues } = useForm<USADeliveryAddressFormutil>();
    // 当满足非SSN且非四个中国时，支持切换邮寄国家即切换不同邮寄地址类型
    const supportToggleDeliveryAddressType = !isSSN && !isFourChn;
    // 展示DeliveryCountry时不需要展示下方的邮寄国家
    const showToggleCountry = supportToggleDeliveryAddressType && hasCountry;
    // BaseAddress的历史逻辑：当hasCountry没有传值时即空值，有默认值true，即展示
    const showLegacyCountry = supportToggleDeliveryAddressType || hasCountry === false ? false : true;

    return (
        <div>
            {title && <h3 className="open-title">{title}</h3>}
            {/* 支持切换邮寄国家：在最上方切换国家来决定邮寄地址类型，展示不同表单 */}
            {showToggleCountry && <DeliveryCountry $defaultValue={deliveryCountry} disabled={disabled} />}
            <BaseAddress prefix="delivery" prefixStreetPlaceholder={__('Mailing')} disabled={disabled} {...restProps} />
            {/* 不支持切换邮寄国家（默认USA不可更改）：美国地址展示是有顺序的，详细地址/州等地址在上方，国家在最后 */}
            {showLegacyCountry && (
                <FormGroup
                    label={__('Country')}
                    name="delivery_country"
                    required={!countryDisabled}
                    $defaultValue="USA"
                    $onFieldChange={() =>
                        $setValues({
                            delivery_state_code: '',
                            delivery_city: ''
                        })
                    }>
                    <CountrySelect isSSN disabled={countryDisabled} fieldName="delivery_country" />
                </FormGroup>
            )}
            <EasyField name="delivery_address_type" type="hidden" $defaultValue={DeliveryAddressTypeEnum.USA} />
        </div>
    );
};

export default connect(USA);
